import React, { memo } from "react";
import { Link } from "react-router-dom";
import "./workspaceLayout.css";

function normalizeActionClassName(value, fallback = "btn btn--ghost") {
  const raw = String(value || fallback).trim() || fallback;

  const mapped = raw
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => {
      switch (token) {
        case "btn-primary":
          return "btn--primary";
        case "btn-ghost":
          return "btn--ghost";
        case "btn-sm":
          return "btn--sm";
        case "btn-outline":
          return "btn--outline";
        case "btn-danger":
          return "btn--danger";
        case "btn-success":
          return "btn--success";
        case "btn-soft":
          return "btn--soft";
        default:
          return token;
      }
    });

  if (!mapped.includes("btn")) {
    mapped.unshift("btn");
  }

  return [...new Set(mapped)].join(" ");
}

function renderAction(action, key, defaultClassName = "btn btn--ghost") {
  if (!action || !action.label) return null;

  const {
    label,
    onClick,
    to,
    disabled = false,
    className = defaultClassName,
    type = "button",
  } = action;

  const finalClassName = normalizeActionClassName(className, defaultClassName);

  if (to) {
    return (
      <Link
        key={key}
        to={disabled ? "#" : to}
        className={finalClassName}
        aria-disabled={disabled ? "true" : undefined}
        tabIndex={disabled ? -1 : undefined}
        onClick={(e) => {
          if (disabled) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          onClick?.(e);
        }}
      >
        {label}
      </Link>
    );
  }

  return (
    <button
      key={key}
      type={type}
      className={finalClassName}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button>
  );
}

function WorkspaceLayout({
  showBack = false,
  backTo = "",
  backLabel = "Back",
  eyebrow = "",
  title,
  subtitle = "",
  status = null,
  meta = [],
  primaryAction = null,
  secondaryActions = [],
  progress = null,
  nextStep = null,
  aiInsight = null,
  tabs = null,
  children,
  stickyAction = null,
  className = "",
}) {
  const hasHeaderMeta = Array.isArray(meta) && meta.length > 0;
  const hasTabs = Boolean(tabs);
  const hasSecondaryActions =
    Array.isArray(secondaryActions) && secondaryActions.length > 0;
  const hasGuidance = Boolean(progress || nextStep || aiInsight);
  const shouldShowBack = Boolean(showBack && backTo);

  return (
    <div className={`wl-page page-shell ${className}`.trim()}>
      <div className="wl-shell page-content">
        <header className="wl-header">
          <div className="wl-headerTop">
            <div className="wl-headerLeft">
              {shouldShowBack ? (
                <Link
                  className="wl-backLink"
                  to={backTo}
                  aria-label={backLabel}
                >
                  <span aria-hidden="true">←</span>
                  <span>{backLabel}</span>
                </Link>
              ) : null}

              <div className="wl-titleWrap">
                {eyebrow ? <div className="wl-eyebrow">{eyebrow}</div> : null}

                <div className="wl-titleRow">
                  <h1 className="wl-title">{title}</h1>
                  {status ? (
                    <div className="wl-statusWrap">{status}</div>
                  ) : null}
                </div>

                {subtitle ? <p className="wl-subtitle">{subtitle}</p> : null}
              </div>
            </div>

            <div className="wl-headerRight">
              {hasSecondaryActions
                ? secondaryActions.map((action, idx) =>
                    renderAction(
                      action,
                      `${action?.label || "secondary"}-${idx}`,
                      "btn btn--ghost"
                    )
                  )
                : null}

              {primaryAction
                ? renderAction(
                    primaryAction,
                    "primary-action",
                    "btn btn--primary"
                  )
                : null}
            </div>
          </div>

          {hasHeaderMeta ? (
            <div className="wl-metaRow" aria-label="Workspace metadata">
              {meta.map((item, idx) => (
                <div
                  key={`${item?.label || "meta"}-${idx}`}
                  className="wl-metaItem"
                >
                  <span className="wl-metaLabel">{item.label}</span>
                  <span className="wl-metaValue">{item.value}</span>
                </div>
              ))}
            </div>
          ) : null}
        </header>

        {hasGuidance ? (
          <section className="wl-guidance">
            {progress ? <div className="wl-guidanceCard">{progress}</div> : null}
            {nextStep ? <div className="wl-guidanceCard">{nextStep}</div> : null}
            {aiInsight ? <div className="wl-guidanceCard">{aiInsight}</div> : null}
          </section>
        ) : null}

        {hasTabs ? (
          <nav className="wl-tabsBar" aria-label="Workspace navigation">
            <div className="wl-tabs">{tabs}</div>
          </nav>
        ) : null}

        <main className="wl-content" id="app-main">
          {children}
        </main>
      </div>

      {stickyAction ? (
        <div className="wl-stickyBar" aria-label="Primary action">
          <div className="wl-shell page-content">
            <div className="wl-stickyInner">{stickyAction}</div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

WorkspaceLayout.displayName = "WorkspaceLayout";

export default memo(WorkspaceLayout);